import {Controller} from "@hotwired/stimulus";
import $ from 'jquery';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static values = {
        isFromAccount: Boolean,
        // DDT-3914: optional GA EventLabel. Empty for the non-booking reject (unchanged);
        // the booking decline sets it to "booking" to tell the two flows apart in reporting.
        eventLabel: String,
    };

    static targets = [
        'step1',
        'step2',
        'messageChoice',
        'message',
        'invalidMessageChoice',
        'invalidMessage',
        'reason',
        'reasonGroup',
        'invalidReason',
        'specialism',
        'freeText',
        'panelSpecialism',
        'panelHorsBarreau',
        'panelAj',
        'panelPause',
        'panelInappropriate',
        'panelFreeText',
        'formContainer',
    ];

    // DDT-3869: reason value -> conditional panel target shown on step 2.
    reasonPanels = {
        legal_issue_does_not_correspond: 'panelSpecialism',
        litigant_dont_reside_with_lawyer_practice_bar: 'panelHorsBarreau',
        lawyer_dont_wish_to_propose_aj_for_this_request: 'panelAj',
        not_available_at_the_moment: 'panelPause',
        not_interested_in_case: 'panelFreeText',
        inappropriate_message: 'panelInappropriate',
        other: 'panelFreeText',
    };

    connect() {
        // The lawyer has reached step 1 (the pop up just opened on the litigant step).
        this.trackEvent('decline_case_step_litigant');

        // DDT-3869: the message-choice field is a fixed list, not a search box.
        // Stop the tom-select input from opening the mobile keyboard so the field
        // only ever opens the dropdown.
        this.suppressKeyboard(this.messageChoiceTarget);
    }

    // Make the tom-select search input non-typable so focusing it never raises the
    // virtual keyboard on mobile and nothing can be typed. The dropdown still opens
    // on tap/click.
    suppressKeyboard(select) {
        const apply = (tomSelect) => {
            const input = tomSelect && tomSelect.control_input;
            if (!input || input.dataset.keyboardSuppressed) {
                return;
            }
            input.dataset.keyboardSuppressed = '1';

            // Stop the mobile virtual keyboard from appearing on focus.
            input.readOnly = true;
            input.setAttribute('inputmode', 'none');

            // tom-select reads characters via its own keydown handler regardless of
            // the readonly attribute, so block printable keys while still allowing
            // navigation/selection of the dropdown.
            const allowedKeys = ['Tab', 'Escape', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
            input.addEventListener('keydown', (event) => {
                if (!allowedKeys.includes(event.key)) {
                    event.preventDefault();
                }
            });
        };

        // Apply now if tom-select is already built, and re-apply on every (re)connect
        // since Symfony UX Autocomplete rebuilds the input when it resets the widget.
        if (select.tomselect) {
            apply(select.tomselect);
        }
        select.addEventListener('autocomplete:connect', (event) => apply(event.detail.tomSelect));
    }

    trackEvent(action) {
        if (typeof dataLayer === 'undefined') {
            return;
        }

        const gaEvent = {
            "category": "Account",
            "action": action,
        };

        // DDT-3914: only the booking decline carries a label; the non-booking reject omits it,
        // keeping its existing (label-less) events untouched.
        if (this.hasEventLabelValue && this.eventLabelValue) {
            gaEvent.label = this.eventLabelValue;
        }

        dataLayer.push({
            "event": "dataLayer.gaEvent",
            "gaEvent": gaEvent,
        });
    }

    messageChoiceOnChange() {
        const value = this.messageChoiceTarget.value;

        if (!value) {
            this.messageTarget.value = '';
        } else {
            const selectedOption = this.messageChoiceTarget.querySelector(`option[value="${value}"]`);

            if (selectedOption) {
                this.messageTarget.value = selectedOption.dataset.prefilledText || '';
            }
        }

        this.messageOnChange();

        if (value) {
            this.messageChoiceTarget.classList.remove('is-invalid');
            this.invalidMessageChoiceTarget.style.setProperty('display', 'none');
        }
    }

    messageOnChange() {
        if (this.messageTarget.value) {
            this.invalidMessageTarget.style.setProperty('display', 'none');
            this.messageTarget.classList.remove('is-invalid');
        } else {
            this.messageTarget.classList.add('is-invalid');
            this.invalidMessageTarget.style.display = 'block';
        }
    }

    goToStep2(event) {
        event && event.preventDefault();

        let hasErrors = false;

        if (!this.messageChoiceTarget.value) {
            this.messageChoiceTarget.classList.add('is-invalid');
            this.invalidMessageChoiceTarget.style.display = 'block';
            hasErrors = true;
        }

        if (!this.messageTarget.value) {
            this.messageTarget.classList.add('is-invalid');
            this.invalidMessageTarget.style.display = 'block';
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        // DDT-3869: persist the step-1 choice now so dropped declines are still recorded.
        this.recordStep1Choice();

        this.step1Target.classList.add('d-none');
        this.step2Target.classList.remove('d-none');

        // The lawyer has reached step 2 (the Justifit step).
        this.trackEvent('decline_case_step_justifit');
    }

    recordStep1Choice() {
        const url = this.step1Target.dataset.step1Url;

        if (!url) {
            return;
        }

        // Fire-and-forget: never block the UX on this analytics write.
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ choice: this.messageChoiceTarget.value }),
        }).catch(() => {});
    }

    reasonOnChange() {
        const checked = this.reasonGroupTarget.querySelector('input:checked');
        const value = checked ? checked.value : null;

        // Hide every conditional panel, then reveal the one for the selected reason.
        // targets.find(): the hors barreau panel is not always rendered (DDT-4147).
        Object.values(this.reasonPanels).forEach((panel) => {
            const target = this.targets.find(panel);
            if (target) {
                target.classList.add('d-none');
            }
        });

        if (value && this.reasonPanels[value]) {
            const target = this.targets.find(this.reasonPanels[value]);
            if (target) {
                target.classList.remove('d-none');
            }
        }

        if (value) {
            this.invalidReasonTarget.style.setProperty('display', 'none');
        }
    }

    submitForm(event) {
        event && event.preventDefault();

        const checked = this.reasonGroupTarget.querySelector('input:checked');

        if (!checked) {
            this.invalidReasonTarget.style.display = 'block';
            return;
        }

        showPreloader();

        // DDT-3869: the decline is being submitted successfully.
        this.trackEvent('decline_case_step_complete');

        if (this.isFromAccountValue) {
            dataLayer.push({
                "event": "dataLayer.gaEvent",
                "gaEvent": {
                    "category": "messages",
                    "action": "messages_chat_reject_case_cta_completed",
                },
            });
        }

        $(this.formContainerTarget).submit();
    }
}
